/**
 * Toggle — BitDrop Single Source of Truth (SSOT) Monochrome Switch Atom.
 * 
 * Strict monochrome design tokens:
 * - Inactive Light: Gray-200 track (#e6e6e6) + White thumb (#ffffff)
 * - Active Light:   Gray-900 track (#161616) + White thumb (#ffffff)
 * - Inactive Dark:  Gray-800 track (#2a2a2a) + Gray-400 thumb (#b3b3b3)
 * - Active Dark:    White track (#ffffff)    + Gray-950 thumb (#0d0d0d)
 */

import React from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Palette } from '../theme/tokens';

export interface ToggleProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
  accessibilityLabel?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
  value,
  onValueChange,
  disabled = false,
  accessibilityLabel = 'Toggle switch',
}) => {
  const { isDark } = useTheme();

  const trackBg = value
    ? (isDark ? Palette.white : Palette.gray900)
    : (isDark ? Palette.gray800 : Palette.gray200);

  const trackBorder = value
    ? (isDark ? Palette.white : Palette.gray900)
    : (isDark ? Palette.gray700 : Palette.gray300);

  const thumbBg = value
    ? (isDark ? Palette.gray950 : Palette.white)
    : (isDark ? Palette.gray400 : Palette.white);

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={() => !disabled && onValueChange(!value)}
      disabled={disabled}
      style={[
        styles.track,
        {
          backgroundColor: trackBg,
          borderColor:     trackBorder,
          alignItems:      value ? 'flex-end' : 'flex-start',
          opacity:         disabled ? 0.5 : 1,
        },
      ]}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      accessibilityLabel={accessibilityLabel}
    >
      <View style={[styles.thumb, { backgroundColor: thumbBg }]} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  track: {
    width:             48,
    height:            28,
    borderRadius:      14,
    borderWidth:       1,
    paddingHorizontal: 3,
    justifyContent:    'center',
  },
  thumb: {
    width:        20,
    height:       20,
    borderRadius: 10,
  },
});
